import { useState, useMemo } from "react";
import { Checkbox, Button, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import FilterFooter from "./FilterFooter";
import SegmentedToggle from "@/components/UI/SegmentedToggle";
import type { CategoricalFilterMode, FilterValue } from "./filterTypes";

export type Option = {
  label: string;
  value: FilterValue;
};

const EMPTY_OPTIONS: Option[] = [];
const EMPTY_SELECTED_VALUES: FilterValue[] = [];

type OptionsProps = {
  options: Option[];
  selectedValues: FilterValue[];
  onChange: (selectedValues: FilterValue[]) => void;
  onApply?: () => void;
  onClose?: () => void;
  onDelete?: () => void;
  showApplyButton?: boolean;
  showSearch?: boolean;
  placeholder?: string;
  maxHeight?: string;
  isDropdown?: boolean;
  useAlternativeLayout?: boolean;
  hideFooter?: boolean;
  disabled?: boolean;
  mode?: CategoricalFilterMode;
  onModeChange?: (mode: CategoricalFilterMode) => void;
};

export const FilterOptions = ({
  options = EMPTY_OPTIONS,
  selectedValues = EMPTY_SELECTED_VALUES,
  onChange,
  onApply,
  onClose,
  onDelete,
  showApplyButton = false,
  showSearch = true,
  placeholder = "Search options...",
  maxHeight = "220px",
  isDropdown = false,
  useAlternativeLayout = false,
  hideFooter = false,
  disabled = false,
  mode = "include",
  onModeChange,
}: OptionsProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const safeSelectedValues = useMemo(
    () =>
      Array.isArray(selectedValues) ? selectedValues : EMPTY_SELECTED_VALUES,
    [selectedValues],
  );

  const filteredOptions = useMemo(() => {
    if (!options || !Array.isArray(options)) return [];
    if (!searchTerm.trim()) return options;

    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [options, searchTerm]);

  const filteredAllChecked = useMemo(() => {
    if (filteredOptions.length === 0) return false;
    return filteredOptions.every((option) =>
      safeSelectedValues.includes(option.value),
    );
  }, [filteredOptions, safeSelectedValues]);

  const filteredSomeChecked = useMemo(() => {
    if (filteredOptions.length === 0) return false;
    return (
      filteredOptions.some((option) =>
        safeSelectedValues.includes(option.value),
      ) && !filteredAllChecked
    );
  }, [filteredOptions, safeSelectedValues, filteredAllChecked]);

  const handleOptionChange = (optionValue: FilterValue, checked: boolean) => {
    let newSelectedValues: FilterValue[];

    if (checked) {
      newSelectedValues = safeSelectedValues.includes(optionValue)
        ? safeSelectedValues
        : [...safeSelectedValues, optionValue];
    } else {
      newSelectedValues = safeSelectedValues.filter(
        (value) => value !== optionValue,
      );
    }

    onChange(newSelectedValues);
  };

  const handleSelectAllFiltered = () => {
    const filteredValues = filteredOptions.map((option) => option.value);
    const newSelectedValues = [
      ...safeSelectedValues.filter(
        (value) => !filteredOptions.some((option) => option.value === value),
      ),
      ...filteredValues,
    ];
    onChange(newSelectedValues);
  };

  const handleDeselectAllFiltered = () => {
    const filteredValues = filteredOptions.map((option) => option.value);
    const newSelectedValues = safeSelectedValues.filter(
      (value) => !filteredValues.includes(value),
    );
    onChange(newSelectedValues);
  };

  const handleDeselectAll = () => {
    onChange([]);
  };

  const handleApplyClick = () => {
    onApply?.();
  };
  const selectedCount = safeSelectedValues.length;
  const totalCount = options.length;

  const isApplyDisabled = selectedCount === 0;

  return (
    <div className="flex flex-col h-full filter-options-custom-checkbox">
      {/* Include/Exclude mode toggle */}
      {onModeChange && (
        <div className="mb-3">
          <SegmentedToggle
            value={mode}
            onChange={(value) => onModeChange(value as CategoricalFilterMode)}
            options={[
              { label: "Include", value: "include" },
              { label: "Exclude", value: "exclude" },
            ]}
            block
            disabled={disabled}
            testId="filter-mode-toggle"
          />
        </div>
      )}

      {/* Header with counts and select/deselect buttons */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
        <span className="text-sm text-gray-600">
          {selectedCount} of {totalCount} selected
        </span>
        <div className="flex gap-2">
          <Button
            type="text"
            size="small"
            onClick={
              disabled
                ? undefined
                : filteredAllChecked
                  ? handleDeselectAllFiltered
                  : handleSelectAllFiltered
            }
            disabled={disabled}
            className="text-xs px-2 py-1 h-auto"
          >
            {filteredAllChecked ? "Deselect All" : "Select All"}
          </Button>
        </div>
      </div>

      {/* Search input */}
      {showSearch && options.length > 5 && !disabled && (
        <div className="mb-3">
          <Input
            placeholder={placeholder}
            prefix={<SearchOutlined className="text-gray-400" />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />
        </div>
      )}

      {/* Select All checkbox for filtered results */}
      {searchTerm && filteredOptions.length > 0 && !disabled && (
        <div className="mb-2 pb-2 border-b border-gray-100">
          <Checkbox
            checked={filteredAllChecked}
            indeterminate={filteredSomeChecked}
            onChange={(e) => {
              if (!disabled) {
                if (e.target.checked) {
                  handleSelectAllFiltered();
                } else {
                  handleDeselectAllFiltered();
                }
              }
            }}
            disabled={disabled}
            className="font-medium text-sm"
          >
            {searchTerm
              ? `All filtered results (${filteredOptions.length})`
              : `All options (${options.length})`}
          </Checkbox>
        </div>
      )}

      {/* Options list */}
      <div
        className="flex-1 overflow-y-auto hide-scrollbar"
        style={{ maxHeight }}
      >
        {filteredOptions.length > 0 ? (
          <div className="space-y-2">
            {filteredOptions.map((option) => {
              const isChecked = safeSelectedValues.includes(option.value);

              return (
                <div
                  key={String(option.value)}
                  className="flex items-center p-2 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={(e: CheckboxChangeEvent) =>
                      !disabled &&
                      handleOptionChange(option.value, e.target.checked)
                    }
                    disabled={disabled}
                    className="shrink-0"
                  >
                    <span
                      className={`ml-2 text-sm ${
                        isChecked
                          ? "font-medium text-[#000000d9]"
                          : "text-gray-700"
                      }`}
                      title={option.label}
                    >
                      {option.label}
                    </span>
                  </Checkbox>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center text-gray-500">
            <SearchOutlined className="text-2xl mb-2" />
            <p className="text-sm">
              {searchTerm
                ? "No options match your search"
                : "No options available"}
            </p>
            {searchTerm && (
              <Button
                type="text"
                size="small"
                onClick={() => setSearchTerm("")}
                className="mt-2"
              >
                Clear search
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Footer with Apply / OK button */}
      {showApplyButton && !hideFooter && (
        <FilterFooter
          onClose={onClose}
          onClear={handleDeselectAll}
          onApply={handleApplyClick}
          onDelete={onDelete}
          isApplyDisabled={isApplyDisabled}
          isClearDisabled={isApplyDisabled}
          isDropdown={isDropdown}
          useAlternativeLayout={useAlternativeLayout}
        />
      )}
    </div>
  );
};

export default FilterOptions;
